import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CustomerObject } from 'src/app/_interfaces/cinema/CustomerObject';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

const API_URL = environment.prismaApiUrl +'/api/dispo/orders';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  currentObject: any;
  isNew: Boolean = false;
  private apiUrl = environment.apiUrl;
  private currentOrderData = new BehaviorSubject<any>(null);
  private hasChanges = new BehaviorSubject<boolean>(false);
  private formValid = new BehaviorSubject<boolean>(false);
  formValid$ = this.formValid.asObservable();
  private linkedOrders = new BehaviorSubject<any[]>([]);
  private classificationData = new BehaviorSubject<any>(null);
  private activeOrderId: number | null = null;

  currentOrderData$ = this.currentOrderData.asObservable();
  hasChanges$ = this.hasChanges.asObservable();
  linkedOrders$ = this.linkedOrders.asObservable();
  classificationData$ = this.classificationData.asObservable();

  constructor(private http: HttpClient) { }

  all(): Observable<any> {
    return this.http.get(API_URL);
  }

  getAll(page: number, perPage: number, filterCustomer: number, filterOrderType: number, filterCustomerBranch: number, searchString: string, filterStatus: number, selectedTab: number, dateFrom?: Date | null, dateTo?: Date | null, filterCreatedAt?: boolean, filterUpdatedAt?: boolean, filterTeamBase?: any[], filterTeamBaseTime?: boolean, filterWorkflowFlags?: string[], filterBranchDirection?: string[], filterTerminDate?: boolean, filterWorkflowFlagsInvert?: boolean): Observable<any> {
    let url = API_URL + '?page=' + page + '&per_page=' + perPage + '&filterCustomer=' + filterCustomer + '&filterOrderType=' + filterOrderType + '&filterCustomerBranch=' + filterCustomerBranch + '&filterStatus=' + filterStatus + '&searchString=' + searchString + '&selectedTab=' + selectedTab;
    
    if (dateFrom) {
      url += '&dateFrom=' + dateFrom.toISOString().split('T')[0];
    }
    if (dateTo) {
      url += '&dateTo=' + dateTo.toISOString().split('T')[0];
    }
    if (filterCreatedAt) {
      url += '&filterCreatedAt=1';
    }
    if (filterUpdatedAt) {
      url += '&filterUpdatedAt=1';
    }
    
    // Team-Base Filter
    if (filterTeamBase && Array.isArray(filterTeamBase) && filterTeamBase.length > 0) {
      filterTeamBase.forEach(id => {
        url += '&filterTeamBase[]=' + id;
      });
    }
    if (filterTeamBaseTime) {
      url += '&filterTeamBaseTime=1';
    }
    if (filterTerminDate) {
      url += '&filterTerminDate=1';
    }
    if (filterWorkflowFlags && Array.isArray(filterWorkflowFlags) && filterWorkflowFlags.length > 0) {
      url += '&filterWorkflowFlags=' + encodeURIComponent(filterWorkflowFlags.join(','));
    }
    if (filterWorkflowFlagsInvert) {
      url += '&filterWorkflowFlagsInvert=1';
    }
    if (filterBranchDirection && Array.isArray(filterBranchDirection) && filterBranchDirection.length > 0) {
      url += '&filterBranchDirection=' + encodeURIComponent(filterBranchDirection.join(','));
    }
    
    return this.http.get(url);
  }

  private buildRollerApiFilterUrl(
    endpoint: string,
    page: number,
    perPage: number,
    filterCustomer: number,
    filterOrderType: number,
    filterCustomerBranch: number,
    searchString: string,
    filterStatus: number,
    selectedTab: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    filterCreatedAt?: boolean,
    filterUpdatedAt?: boolean,
    filterTeamBase?: any[],
    filterTeamBaseTime?: boolean
  ): string {
    let url = API_URL + endpoint + '?page=' + page + '&per_page=' + perPage + '&filterCustomer=' + filterCustomer + '&filterOrderType=' + filterOrderType + '&filterCustomerBranch=' + filterCustomerBranch + '&filterStatus=' + filterStatus + '&searchString=' + encodeURIComponent(searchString || '') + '&selectedTab=' + selectedTab;

    if (dateFrom) {
      url += '&dateFrom=' + dateFrom.toISOString().split('T')[0];
    }
    if (dateTo) {
      url += '&dateTo=' + dateTo.toISOString().split('T')[0];
    }
    if (filterCreatedAt) {
      url += '&filterCreatedAt=1';
    }
    if (filterUpdatedAt) {
      url += '&filterUpdatedAt=1';
    }
    if (filterTeamBase && Array.isArray(filterTeamBase) && filterTeamBase.length > 0) {
      filterTeamBase.forEach(id => {
        url += '&filterTeamBase[]=' + id;
      });
    }
    if (filterTeamBaseTime) {
      url += '&filterTeamBaseTime=1';
    }

    return url;
  }

  rollerApiCheckList(
    page: number,
    perPage: number,
    filterCustomer: number,
    filterOrderType: number,
    filterCustomerBranch: number,
    searchString: string,
    filterStatus: number,
    selectedTab: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    filterCreatedAt?: boolean,
    filterUpdatedAt?: boolean,
    filterTeamBase?: any[],
    filterTeamBaseTime?: boolean
  ): Observable<any> {
    const url = this.buildRollerApiFilterUrl(
      '/roller-api-check-list',
      page,
      perPage,
      filterCustomer,
      filterOrderType,
      filterCustomerBranch,
      searchString,
      filterStatus,
      selectedTab,
      dateFrom,
      dateTo,
      filterCreatedAt,
      filterUpdatedAt,
      filterTeamBase,
      filterTeamBaseTime
    );
    return this.http.get(url);
  }

  rollerApiCheckOrder(orderId: number): Observable<any> {
    return this.http.post(API_URL + '/' + orderId + '/roller-api-check', {});
  }

  rollerApiBulkCheck(
    page: number,
    perPage: number,
    filterCustomer: number,
    filterOrderType: number,
    filterCustomerBranch: number,
    searchString: string,
    filterStatus: number,
    selectedTab: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    filterCreatedAt?: boolean,
    filterUpdatedAt?: boolean,
    filterTeamBase?: any[],
    filterTeamBaseTime?: boolean
  ): Observable<any> {
    const url = this.buildRollerApiFilterUrl(
      '/roller-api-bulk-check',
      page,
      perPage,
      filterCustomer,
      filterOrderType,
      filterCustomerBranch,
      searchString,
      filterStatus,
      selectedTab,
      dateFrom,
      dateTo,
      filterCreatedAt,
      filterUpdatedAt,
      filterTeamBase,
      filterTeamBaseTime
    );

    return this.http.post(url, {});
  }

  killAllKillables(): Observable<any> {
    return this.http.get<CustomerObject>(API_URL + '/kill-all-killables');
  } 

  genBackup(): Observable<any> {
    return this.http.get<CustomerObject>(API_URL + '/gen-backup');
  } 

  storeClassParams(id:any, object: any): Observable<any> {
    return this.http.post<CustomerObject>(API_URL + '/store-class-params/' + id, {data: object});
  }

  storeDuration(id: any, object: any): Observable<any> {
    return this.http.post<CustomerObject>(API_URL + '/store-duration/' + id, {data: object});
  }

  storeRekla(id: any, object: any): Observable<any> {
    return this.http.post<CustomerObject>(API_URL + '/store-rekla/' + id, {data: object});
  }

  getGeoCoords(id: any): Observable<any> {
    return this.http.get<CustomerObject>(API_URL + '/get-geo-coords/' + id);
  }

  /** Aktuelle Aufträge ohne Geokoordinaten. */
  listMissingGeocoords(params?: {
    customer_id?: number;
    customer_branch_id?: number;
  }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.customer_id != null && params.customer_id > 0) {
      httpParams.customer_id = String(params.customer_id);
    }
    if (params?.customer_branch_id != null && params.customer_branch_id > 0) {
      httpParams.customer_branch_id = String(params.customer_branch_id);
    }
    return this.http.get(API_URL + '/missing-geocoords', {
      params: Object.keys(httpParams).length ? httpParams : undefined,
    });
  }

  /** Offene Aufträge ohne Termin einer Filiale (mit Koordinaten für Karte). */
  listUnscheduledByBranch(branchId: number): Observable<{ data: any[]; count: number }> {
    return this.http.get<{ data: any[]; count: number }>(
      API_URL + '/unscheduled-by-branch/' + branchId
    );
  }

  /** API: Koordinaten für Auftrag aus Adresse ermitteln und in der DB speichern. */
  geocodeOrderCoords(orderId: number): Observable<any> {
    return this.http.post<CustomerObject>(API_URL + '/geocode-coords/' + orderId, {});
  }

  /** API: Route berechnen (Directions) – Waypoints [{lat, lng}, ...]. Liefert distance_text, duration_text, path. */
  getRouteDirections(
    waypoints: { lat: number; lng: number }[],
    optimize = false
  ): Observable<any> {
    const baseUrl = environment.prismaApiUrl + '/api/dispo';
    return this.http.post<{ distance_text: string; duration_text: string; path: { lat: number; lng: number }[] }>(
      baseUrl + '/directions',
      { waypoints, optimize }
    );
  }

  /** Gespeicherte Tages-Tour (Resource, team_base_id, Strecke, Stopps mit team_order_id & Teilstrecken). */
  saveTeamTour(payload: Record<string, unknown>): Observable<any> {
    const baseUrl = environment.prismaApiUrl + '/api/dispo';
    return this.http.post(baseUrl + '/team-tours', payload);
  }

  /** Tour abschließen (Kalender-Häkchen); setzt completed_at serverseitig. */
  completeTeamTour(id: number): Observable<{ success?: boolean; team_tour?: any }> {
    const baseUrl = environment.prismaApiUrl + '/api/dispo';
    return this.http.post<{ success?: boolean; team_tour?: any }>(
      `${baseUrl}/team-tours/${id}/complete`,
      {}
    );
  }

  /** Alle DB-Touren für Kalender-Woche (nur gespeicherte). */
  getTeamToursForCalendarRange(
    start: string,
    end: string,
    teamBaseIds: number[]
  ): Observable<{ team_tours: any[] }> {
    const baseUrl = environment.prismaApiUrl + '/api/dispo';
    return this.http.post<{ team_tours: any[] }>(baseUrl + '/team-tours/for-calendar-range', {
      start,
      end,
      team_base_ids: teamBaseIds,
    });
  }

  getOrderHitory(id: any, object: any): Observable<any> {
    return this.http.post<CustomerObject>(API_URL + '/get-order-history/' + id, {data: object});
  }


  getFilter(): Observable<any> {
    return this.http.get(API_URL + '/filter');
  }

  getFields(): Observable<any> {
    return this.http.get(API_URL + '/get_fields');
  }

  search(string): Observable<any> {
    return this.http.get(API_URL + '/search/' + string);
  }

  export(): Observable<any> {
    return this.http.get(API_URL + '/export',{responseType: 'blob'});
  }

  send(): Observable<any> {
    return this.http.get(API_URL + '/send');
  }

  store(object: any): Observable<any> {
    console.log('Store aufgerufen mit:', object);
    const orderData = {
      user_id: 1,
      order_type_id: object.order_type_id,
      customer_id: object.customer_id,
      customer_branch_id: object.customer_branch_id,
      order_number: object.order_number,
      avis_date: object.avis_date,
      forcast: object.forcast,
      company: object.company,
      status_id: object.status_id || 1,
      color: null,
      archive: null,
      is_prime: true,
      gender_id: object.gender_id,
      first_name: object.first_name,
      last_name: object.last_name,
      street: object.street,
      postal_code: object.postal_code,
      place: object.place,
      country_id: object.country_id,
      phone: object.phone,
      phone_business: object.phone_business,
      mobile: object.mobile,
      fax: object.fax,
      email: object.email
    };
    console.log('Sende an API:', {data: orderData});
    return this.http.post<CustomerObject>(API_URL, {data: orderData});
  }

  /**
   * Auftrag vollständig duplizieren (serverseitig: Person, RootOrder, Klassifikation, Termine, Ladeorte, neue KV-Nr.).
   */
  duplicateOrder(orderId: number | string): Observable<any> {
    return this.http.post<CustomerObject>(`${API_URL}/${orderId}/duplicate`, {});
  }

  /** Rekla-/Sonderauftrag anlegen (Legacy-Endpoint, nicht vollständige Kopie). */
  copy(object: any): Observable<any> {
    if (!object || typeof object !== 'object') {
      return this.http.post<CustomerObject>(API_URL + '/copy', { data: {} });
    }
    const data = { ...object };
    delete data.id;
    return this.http.post<CustomerObject>(API_URL + '/copy', { data });
  }

  dispose(id: any): Observable<any> {
    return this.http.get(API_URL + '/dispose/' + id);
  }

  archive(id: any): Observable<any> {
    return this.http.get(API_URL + '/archive/' + id);
  }

  delete(object: any): Observable<any> {
    return this.http.delete(API_URL + '/' + object.id);
  }

  cacheOrders(): Observable<any> {
    return this.http.get(API_URL + '/cache-orders');
  }

  show(id: any): Observable<any> {
    this.currentObject = this.http.get(API_URL + '/' + id);
    // Merke die aktuell geladene Order-ID frühzeitig
    this.setActiveOrderId(Number(id));
    return this.currentObject;
  }

  undispose(id: any): Observable<any> {
    this.currentObject = this.http.get(API_URL + '/undispose/' + id);
    return this.currentObject;
  }

  update(object: any): Observable<any> {
    console.log('Update aufgerufen mit:', object);
    
    // Extrahiere die base-Daten
    const baseData = object.base || object;
    
    // Erstelle die korrekte Datenstruktur für das Update
    const updateData = {
      user_id: 1,
      order_type_id: baseData.order_type_id,
      customer_id: baseData.customer_id,
      customer_branch_id: baseData.customer_branch_id,
      order_number: baseData.order_number,
      avis_date: baseData.avis_date,
      forcast: baseData.forcast,
      company: baseData.company,
      status_id: (this.currentOrderData.value?.base?.status_id) ?? baseData.status_id,
      color: baseData.color,
      archive: baseData.archive,
      is_prime: true,
      // Klassifikations-/Berechnungsfelder optional mitschicken
      duration: baseData.duration,
      order_value: baseData.order_value,
      amount_assembly_units: baseData.amount_assembly_units,
      amount_connections: baseData.amount_connections,
      amount_total: baseData.amount_total,
      rev_delivery: baseData.rev_delivery,
      rev_assembly: baseData.rev_assembly,
      rev_connections: baseData.rev_connections,
      rev_others: baseData.rev_others,
      rev_total: baseData.rev_total,
      order_params: baseData.order_params,
      order_calc: baseData.order_calc,
      order_goods: baseData.order_goods,
      order_goods_total: baseData.order_goods_total,
      order_calcs_total: baseData.order_calcs_total,
      rekla_market_consult: baseData.rekla_market_consult,
      rekla_last_team_id: baseData.rekla_last_team_id,
      root_order: {
        order_number: baseData.order_number,
        user_id: 1,
        customer_id: baseData.customer_id,
        customer_branch_id: baseData.customer_branch_id,
        person: {
          gender_id: baseData.gender_id,
          first_name: baseData.first_name,
          last_name: baseData.last_name,
          street: baseData.street,
          postal_code: baseData.postal_code,
          place: baseData.place,
          country_id: baseData.country_id,
          company: baseData.company,
          phone: baseData.phone,
          phone_business: baseData.phone_business,
          mobile: baseData.mobile,
          fax: baseData.fax,
          email: baseData.email,
          level: baseData.level
        }
      }
    };

    console.log('Sende Update an API:', {data: updateData});
    return this.http.put<CustomerObject>(API_URL + '/' + object.id, {data: updateData});
  }

  genPDF(object: any, columns: any, header: any): Observable<any> {
    console.log(columns);
    return this.http.post(API_URL + '/gen-pdf', {data: object.data, columns: columns, header: header}, {responseType: 'blob'});
  }

  genBillingPDF(object: any, billing: any, options: any): Observable<any> {
    return this.http.post(API_URL + '/gen-billing-pdf', {data: object.data, billing: billing, options: options}, {responseType: 'blob'});
  }

  getCurrentObject() {
    return this.currentObject;
  }


  setIsNew(value: any) {
    this.isNew = value;
    if (value === true) {
      // Bei neuem Auftrag sicherstellen, dass keine alten Klassifikations-/Link-Daten hängen bleiben
      this.clearClassificationData();
      this.linkedOrders.next([]);
    }
  }

  getIsNew() {
    return this.isNew;
  }

  // Neue Methoden für die zentrale Datenverwaltung
  setCurrentOrderData(data: any) {
    console.log('setCurrentOrderData aufgerufen mit:', data);
    this.currentOrderData.next(data);
    // Versuche aktive Order-ID aus den Daten zu setzen
    const id = data?.id || data?.base?.id || data?.data?.id;
    if (id) {
      this.setActiveOrderId(Number(id));
    }
  }

  getCurrentOrderData() {
    return this.currentOrderData.asObservable();
  }

  updateComponentData(componentName: string, data: any) {
    console.log('updateComponentData aufgerufen mit:', { componentName, data });
    const currentData = this.currentOrderData.value || {};
    const updatedData = {
      ...currentData,
      [componentName]: data
    };
    console.log('Aktualisierte Daten:', updatedData);
    this.currentOrderData.next(updatedData);
    this.hasChanges.next(true);
  }

  // Neue Methode zum Markieren von Änderungen
  markAsChanged() {
    this.hasChanges.next(true);
  }

  getComponentData(componentName: string) {
    const currentData = this.currentOrderData.value;
    return currentData ? currentData[componentName] : null;
  }

  saveAllChanges() {
    const currentData = this.currentOrderData.value;
    const classificationData = this.classificationData.value;
    console.log('saveAllChanges - Aktuelle Daten:', currentData);
    console.log('saveAllChanges - Classification Daten:', classificationData);
    
    if (currentData) {
      // Extrahiere die ID aus den verschiedenen möglichen Stellen
      let orderId = currentData.id || 
                    (currentData.base && currentData.base.id) || 
                    (currentData.data && currentData.data.id);
      // Erzwinge die aktive Order-ID, falls vorhanden, um Fehlzuordnung zu verhindern
      if (this.activeOrderId) {
        orderId = this.activeOrderId;
      }
      
      console.log('Gefundene Order-ID:', orderId);

      if (orderId) {
        console.log('Update vorhandener Auftrag mit ID:', orderId);
        // Stelle sicher, dass die status_id korrekt übergeben wird
        // Zusätzlich: Klassifikationsdaten (z. B. order_value, duration, amount_*, rev_*) in den Update-Payload mergen
        const updateData = {
          ...currentData.base,
          ...(classificationData || {}),
          status_id: currentData.base ? currentData.base.status_id : null, // << explizit setzen
          id: orderId
        };
        return this.update(updateData).pipe(
          map(response => {
            // Speichere Classification-Daten nach dem Update der Hauptdaten
            if (classificationData && response && response.data) {
              console.log('Speichere Classification-Daten für Order ID:', response.data.id);
              // Bestimme ob es Rekla oder Duration Daten sind
              const cd: any = { ...classificationData };
              if (cd.duration === undefined) {
                cd.duration = (currentData.base && currentData.base.duration != null)
                  ? currentData.base.duration
                  : (currentData.duration != null ? currentData.duration : null);
              }
              if (cd.rekla_todo !== undefined) {
                // Rekla Daten
                this.storeRekla(response.data.id, cd).subscribe(
                  classificationResponse => {
                    console.log('Classification (Rekla) gespeichert:', classificationResponse);
                  },
                  error => {
                    console.error('Fehler beim Speichern der Classification (Rekla):', error);
                  }
                );
              } else {
                // Duration Daten
                this.storeDuration(response.data.id, cd).subscribe(
                  classificationResponse => {
                    console.log('Classification (Duration) gespeichert:', classificationResponse);
                  },
                  error => {
                    console.error('Fehler beim Speichern der Classification (Duration):', error);
                  }
                );
              }
            }
            // Falls keine Classification-Daten vorliegen, aber eine Dauer vorhanden ist, separat speichern
            if ((!classificationData || Object.keys(classificationData || {}).length === 0) && response && response.data) {
              const dur = (currentData.base && currentData.base.duration != null)
                ? currentData.base.duration
                : (currentData.duration != null ? currentData.duration : null);
              if (dur != null) {
                const payload: any = { duration: dur };
                if (response.data.order_type_id === 0) {
                  this.storeRekla(response.data.id, payload).subscribe({
                    next: () => console.log('Duration (Rekla) separat gespeichert'),
                    error: (e) => console.warn('Duration (Rekla) konnte nicht separat gespeichert werden', e)
                  });
                } else {
                  this.storeDuration(response.data.id, payload).subscribe({
                    next: () => console.log('Duration separat gespeichert'),
                    error: (e) => console.warn('Duration konnte nicht separat gespeichert werden', e)
                  });
                }
              }
            }
            // Verknüpfte Aufträge speichern, falls vorhanden
            const linked = this.getCurrentLinkedOrders();
            if (Array.isArray(linked) && linked.length > 0) {
              this.storeLinkedOrders(orderId, linked).subscribe({
                next: () => console.log('Linked orders gespeichert'),
                error: (err) => console.warn('Linked orders konnten nicht gespeichert werden', err)
              });
            } else {
              // Falls leer, serverseitig bestehende löschen
              this.storeLinkedOrders(orderId, []).subscribe({
                next: () => console.log('Linked orders geleert'),
                error: (err) => console.warn('Linked orders leeren fehlgeschlagen', err)
              });
            }

            this.resetChanges();
            this.clearClassificationData();
            return response;
          })
        );
      } else {
        // Wenn keine ID vorhanden ist, neuen Auftrag speichern
        const baseData = {
          ...currentData.base,
          status_id: currentData.base ? currentData.base.status_id : null // << explizit setzen
        };
        console.log('Speichere neue Daten:', baseData);
        return this.store(baseData).pipe(
          map(response => {
            // WICHTIG: Für neu angelegte Aufträge KEINE vorherige Klassifikation übernehmen
            // (Anforderung: order-classification muss leer sein)
            this.clearClassificationData();
            // Falls keine Classification-Daten vorliegen, aber eine Dauer vorhanden ist, separat speichern
            if ((!classificationData || Object.keys(classificationData || {}).length === 0) && response && response.data) {
              const dur = (currentData.base && currentData.base.duration != null)
                ? currentData.base.duration
                : (currentData.duration != null ? currentData.duration : null);
              if (dur != null) {
                const payload: any = { duration: dur };
                if (response.data.order_type_id === 0) {
                  this.storeRekla(response.data.id, payload).subscribe({
                    next: () => console.log('Duration (Rekla) separat gespeichert (neu)'),
                    error: (e) => console.warn('Duration (Rekla) konnte nicht separat gespeichert werden (neu)', e)
                  });
                } else {
                  this.storeDuration(response.data.id, payload).subscribe({
                    next: () => console.log('Duration separat gespeichert (neu)'),
                    error: (e) => console.warn('Duration konnte nicht separat gespeichert werden (neu)', e)
                  });
                }
              }
            }
            // Verknüpfte Aufträge im Anschluss anlegen
            const newId = response?.data?.id;
            const linked = this.getCurrentLinkedOrders();
            if (newId && Array.isArray(linked)) {
              this.storeLinkedOrders(newId, linked).subscribe({
                next: () => console.log('Linked orders für neuen Auftrag gespeichert'),
                error: (err) => console.warn('Linked orders für neuen Auftrag fehlgeschlagen', err)
              });
            }

            this.resetChanges();
            if (response && response.data) {
              this.currentOrderData.next({
                ...currentData,
                ...response.data
              });
              // Setze aktive Order-ID auf den neu erzeugten Datensatz
              this.setActiveOrderId(response.data.id);
            }
            return response;
          })
        );
      }
    }
    return null;
  }

  resetChanges() {
    this.hasChanges.next(false);
    this.clearClassificationData();
  }

  // Methoden für verknüpfte Aufträge
  setLinkedOrders(linkedOrders: any[]) {
    this.linkedOrders.next(linkedOrders);
  }

  getCurrentLinkedOrders() {
    return this.linkedOrders.value;
  }

  matchOrdersByNumbers(params: {
    numbers: string[];
    customer_branch_id?: number | null;
    order_type_ids?: number[];
  }): Observable<{
    matches: Record<
      string,
      {
        exists: boolean;
        order_id: number | null;
        order_number?: string;
        order_type_id?: number | null;
        customer_branch_id?: number | null;
        status_id?: number | null;
      }
    >;
    matched?: number;
    total?: number;
  }> {
    return this.http.post<any>(`${API_URL}/match-by-numbers`, {
      numbers: params.numbers || [],
      customer_branch_id: params.customer_branch_id ?? undefined,
      order_type_ids: params.order_type_ids ?? undefined,
    });
  }

  checkOrderByNumber(orderNumber: string): Observable<any> {
    return this.http.get(`${API_URL}/check-order-by-number/${encodeURIComponent(orderNumber)}`);
  }

  getOrderByNumber(orderNumber: string): Observable<any> {
    return this.http.get(`${API_URL}/get-order-by-number/${encodeURIComponent(orderNumber)}`);
  }

  applyDatasetExtract(
    orderId: number,
    fields: {
      overwrite?: boolean;
      first_name?: string;
      last_name?: string;
      street?: string;
      postal_code?: string;
      place?: string;
      phone?: string;
      mobile?: string;
      level?: string;
      email?: string;
      number?: string;
    }
  ): Observable<any> {
    return this.http.post(`${API_URL}/${orderId}/apply-dataset-extract`, fields);
  }

  applyRollerPositions(
    orderId: number,
    payload: {
      overwrite?: boolean;
      positions: Array<{
        position?: string;
        artikelnummer?: string;
        artikelbezeichnung?: string;
        ausfuehrung?: string;
        warengruppe?: string;
        liefermenge?: string;
        montagemenge?: string;
        montageeinheiten?: string;
        lieferkostenVk?: string;
        lieferkostenEk?: string;
        montagekostenVk?: string;
        montagekostenEk?: string;
        warenwert?: string;
        reklamationstext?: string;
        termin?: string;
        tageszeit?: string;
        monteur?: string;
        monteurSchluessel?: string;
        zusatztext?: string;
      }>;
    }
  ): Observable<any> {
    return this.http.post(`${API_URL}/${orderId}/apply-roller-positions`, payload);
  }

  applyKaufvertragArticles(
    orderId: number,
    payload: {
      overwrite?: boolean;
      document_id?: number;
      run_id?: number;
      articles: Array<{
        position?: string;
        artikelbezeichnung?: string;
        beschreibung?: string;
        bezeichnung?: string;
        laenge?: string;
        breite?: string;
        hoehe?: string;
        anzahl?: string;
        preis?: string;
        document_id?: number;
      }>;
    }
  ): Observable<any> {
    return this.http.post(`${API_URL}/${orderId}/apply-kaufvertrag-articles`, payload);
  }

  applyClassification500Items(
    orderId: number,
    payload: {
      overwrite?: boolean;
      run_id?: number;
      translation?: string;
      summary?: string;
      items: Array<{
        position?: string;
        artikelnummer?: string;
        artikelbezeichnung?: string;
        original?: string;
        translated?: string;
        notes?: string;
        termin?: string;
        tageszeit?: string;
        monteur?: string;
        monteurSchluessel?: string;
        zusatztext?: string;
        selected?: boolean;
      }>;
    }
  ): Observable<any> {
    return this.http.post(`${API_URL}/${orderId}/apply-classification-500-items`, payload);
  }

  updateOrderWorkflowFlag(
    orderId: number,
    flag: 'kaufvertrag_ai' | 'order_data_captured' | 'documents_uploaded' | 'classified' | 'roller_portal_synced' | 'images_protocols' | 'needs_review',
    checked: boolean,
    note?: string | null
  ): Observable<any> {
    const payload: { flag: string; checked: boolean; note?: string } = { flag, checked };
    if (note != null && String(note).trim() !== '') {
      payload.note = String(note).trim();
    }
    return this.http.post(`${API_URL}/${orderId}/workflow-flags`, payload);
  }

  batchCreateOrders(payload: {
    customer_branch_id: number;
    order_type_id: number;
    tour_date: string; // YYYYMMDD (z. B. 20260731)
    branchNo: string;
    roller_api_check?: boolean;
    items: Array<{
      kv_no: string;
      roller_fields?: {
        first_name?: string;
        last_name?: string;
        street?: string;
        postal_code?: string;
        place?: string;
        number?: string;
        level?: string;
        phone?: string;
        mobile?: string;
        email?: string;
      };
    }>;
  }): Observable<any> {
    return this.http.post(`${API_URL}/batch-create`, payload);
  }

  storeLinkedOrders(orderId: number, linkedOrders: any[]): Observable<any> {
    return this.http.post(`${API_URL}/${orderId}/linked-orders`, {
      linked_orders: linkedOrders
    });
  }

  getLinkedOrders(orderId: number): Observable<any> {
    return this.http.get(`${API_URL}/${orderId}/linked-orders`);
  }

  // Neue Methode zum Aktualisieren des Auftragsstatus
  updateStatus(orderId: number, statusId: number): Observable<any> {
    return this.http.put(`${API_URL}/${orderId}/status`, {
      status_id: statusId
    });
  }

  // Notiz speichern (Common Notes API)
  storeNote(moduleId: number, moduleItemId: number, title: string, description: string): Observable<any> {
    const payload = {
      data: {
        module_id: moduleId,
        module_item_id: moduleItemId,
        title: title,
        description: description
      }
    } as any;
    return this.http.post(`${environment.prismaApiUrl}/api/dispo/notes`, payload);
  }

  // Neue Methoden für Classification-Daten
  setClassificationData(data: any): void {
    const current = this.classificationData.value || {};
    this.classificationData.next({ ...current, ...data });
    this.hasChanges.next(true);
  }

  getClassificationData(): any {
    return this.classificationData.value;
  }

  clearClassificationData(): void {
    this.classificationData.next(null);
  }

  getSuggestedAppointments(params: any): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params.team_id) queryParams.append('team_id', params.team_id);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.days_ahead) queryParams.append('days_ahead', params.days_ahead);
    
    return this.http.get(API_URL + '/suggested-appointments?' + queryParams.toString());
  }

  // Aktive Order-ID setzen/lesen/Reset für Konsistenz zwischen Tabs
  setActiveOrderId(id: number | null): void {
    this.activeOrderId = id ?? null;
  }

  getActiveOrderId(): number | null {
    return this.activeOrderId;
  }

  // Kompletter Reset aller im Service gecachten Order-States
  setFormValid(valid: boolean): void {
    this.formValid.next(valid);
  }

  resetAllState(): void {
    this.currentOrderData.next(null);
    this.hasChanges.next(false);
    this.formValid.next(false);
    this.linkedOrders.next([]);
    this.classificationData.next(null);
    this.activeOrderId = null;
    this.currentObject = null;
  }
}
